import React, { useCallback, useEffect, useRef } from 'react';
import { Form, FormSpy } from 'react-final-form';
import { Box, Grid, Typography } from 'housecall-ui';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';

import { Cart } from '../../components/Cart/Cart';
import { Layout } from '../../components/Layout';
import { ScrollableContent } from '../../components/ScrollableContent';
import { StepProgressBar } from '../../components/StepProgressBar';
import { useChangeStep } from '../../hooks/useChangeStep';
import { useOrgData } from '../../hooks/useOrgData';
import { ContactDetailsValues } from '../../state/types';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';
import { sentryIdentifyUser } from '../../utils/external-libs/sentry';
import { BackArrowButton } from '../BackArrowButton';

import { AddressFields } from './AddressFields';
import { CA_STATES_LIST, CANADA, US_STATES_LIST } from './consts';
import { ExistingCustomerFields } from './ExistingCustomerFields';
import { MarketingConsent } from './MarketingConsenst';
import { NewCustomerFields } from './NewCustomerFields';
import { onValidate } from './validation';

type Mutators = Record<string, (...args: unknown[]) => unknown>;

const navigateToFirstFormError = (form: HTMLFormElement) => {
  for (let i = 0; i < form.length; i += 1) {
    const isInputInvalid = form[i]?.ariaInvalid === 'true';

    if (isInputInvalid) {
      form[i]?.scrollIntoView({ behavior: 'smooth' });
      break;
    }
  }
};

const clearStateValue = (values: ContactDetailsValues, mutators: Mutators) => {
  if (!values.state) {
    return;
  }
  // clears the state value if country was changed
  if (values.country && (values.country === CANADA ? US_STATES_LIST : CA_STATES_LIST).includes(values.state)) {
    if (mutators.clearCountryState) {
      mutators.clearCountryState();
    }
  }
};

export const ContactDetailsStep = () => {
  const { state, dispatch, actions } = useOnlineBookingReducer();
  const { initParams, token } = state;
  const { data: orgData } = useOrgData({ token });

  const isCustomerPortal = initParams?.source === 'customer_portal';
  const { changeStep } = useChangeStep();

  const { isUnscheduled } = useOrgData({ token });

  const formRef = useRef<HTMLFormElement>(null);

  const formOnChange = useCallback(
    ({ dirty, values, valid }, mutators) => {
      if (dirty) {
        clearStateValue(values, mutators);
        dispatch(actions.updateContactDetailsValues(values));
        dispatch(actions.updateContactDetailsValid(valid));
      }
    },
    [actions, dispatch]
  );

  useEffect(() => {
    if (state.contactDetails.submitting && formRef.current) {
      // @see https://final-form.org/docs/react-final-form/faq#via-documentgetelementbyid
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      dispatch(actions.stopSubmittingContactDetailsForm());
    }
  }, [state.contactDetails.submitting, actions, dispatch]);

  useEffect(() => {
    if (state.contactDetails.submitting && formRef.current && !state.contactDetails.valid) {
      setTimeout(() => {
        if (formRef.current) navigateToFirstFormError(formRef.current);
        // Timeout is required, because we need to render errors on screen and then move to it
      }, 100);
    }
  }, [state.contactDetails.submitting, state.contactDetails.valid, actions, dispatch]);

  const handleFormSubmit = () => {
    if (state.contactDetails.valid) {
      if (state?.contactDetails?.values?.email) {
        sentryIdentifyUser({ email: state.contactDetails.values.email });
      }
      changeStep(isUnscheduled ? 'confirmation' : 'booking'); // booking or confirmation step depending on the flow
    }
  };

  const handleBackClick = () => {
    changeStep('services-troubleshooting');
  };

  const handleNext = () => {
    dispatch(actions.startSubmittingContactDetailsForm());
  };

  return (
    <Layout
      ContentComponent={
        <>
          <BackArrowButton onClick={handleBackClick} />
          <ScrollableContent>
            <Typography gutterBottom variant="h5">
              Add your contact details
            </Typography>

            <Form<ContactDetailsValues>
              onSubmit={handleFormSubmit}
              initialValues={state.contactDetails.values}
              validate={onValidate(isCustomerPortal)}
              mutators={{
                clearCountryState: (_args, formState, utils) => {
                  utils.changeValue(formState, 'state', () => '');
                },
              }}
            >
              {({ handleSubmit, values, form }) => (
                <form ref={formRef} onSubmit={handleSubmit} aria-label="Contact details">
                  <Grid container spacing={1} />

                  {initParams?.source === 'customer_portal' ? (
                    <ExistingCustomerFields customerPortalParams={initParams} />
                  ) : (
                    <NewCustomerFields />
                  )}

                  <Box mb={2}>
                    <Typography variant="body1">
                      Please add the address where you want the service to take place.
                    </Typography>
                  </Box>

                  <AddressFields country={values.country}>
                    <Grid item xs={12} md={12}>
                      <Box pt={1.75}>
                        <MarketingConsent
                          label={`By checking this box, I consent to receive marketing and promotional text messages from ${orgData?.name} at the phone number I have provided above. Message and data rates may apply. Message frequency varies. Reply HELP for help and STOP to stop.`}
                        />
                      </Box>
                    </Grid>
                  </AddressFields>

                  <FormSpy<ContactDetailsValues>
                    subscription={{ dirty: true, values: true, valid: true }}
                    onChange={(args) => formOnChange(args, form.mutators)}
                  />
                </form>
              )}
            </Form>
          </ScrollableContent>
        </>
      }
      Buttons={
        <Button version="v2" size="medium" fullWidth variant="contained" color="secondary" onClick={handleNext}>
          Next
        </Button>
      }
      Header={
        <>
          <StepProgressBar currentStep={isUnscheduled ? 4 : 3} stepCount={5} />
          <Cart />
        </>
      }
    />
  );
};
